import React, { useState } from 'react';
import { Pencil, Check, X, RotateCcw } from 'lucide-react';

/**
 * Inline-editable field with override indicator.
 *
 * Props:
 *  - label: string
 *  - effectiveValue: string - what to display (override or fallback)
 *  - squareValue: string - the Person/Square value (for reference)
 *  - overrideValue: string|null - the raw override (null = not overridden)
 *  - overrideKey: string - the API field name (e.g., "override_first_name")
 *  - onSave: (overrideKey, newValue) => Promise<void>
 *  - inputType: "text" | "email" | "tel" | "textarea" (default "text")
 */
function EditableField({
  label,
  effectiveValue,
  squareValue,
  overrideValue,
  overrideKey,
  onSave,
  inputType = 'text',
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const isOverridden =
    overrideValue !== null && overrideValue !== '' && overrideValue !== undefined;

  const startEdit = () => {
    setValue(overrideValue || effectiveValue || '');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(overrideKey, value);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    setSaving(true);
    try {
      await onSave(overrideKey, null);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputType !== 'textarea') {
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  };

  if (editing) {
    const isTextarea = inputType === 'textarea';
    return (
      <div>
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <div className={`flex ${isTextarea ? 'flex-col' : 'items-center'} gap-1`}>
          {isTextarea ? (
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-xs border border-gray-300 rounded px-1.5 py-1 focus:ring-1 focus:ring-red-800 focus:border-transparent w-full resize-none"
              rows={3}
              autoFocus
            />
          ) : (
            <input
              type={inputType}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-xs border border-gray-300 rounded px-1.5 py-0.5 focus:ring-1 focus:ring-red-800 focus:border-transparent w-full"
              autoFocus
            />
          )}
          <div className="flex items-center gap-0.5">
            <button
              onClick={save}
              disabled={saving}
              className="text-green-600 hover:text-green-800 p-0.5 disabled:opacity-50"
              title="Save"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={cancel}
              className="text-gray-400 hover:text-gray-600 p-0.5"
              title="Cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            {isOverridden && (
              <button
                onClick={clearOverride}
                disabled={saving}
                className="text-amber-500 hover:text-amber-700 p-0.5 disabled:opacity-50"
                title="Revert to Square value"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {isOverridden && squareValue && (
          <p className="text-xs text-gray-400 mt-0.5 italic">
            Square: {squareValue}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="group">
      <p className="text-xs text-gray-500 mb-0.5">
        {label}
        {isOverridden && (
          <span
            className="ml-1 inline-block w-1.5 h-1.5 bg-amber-400 rounded-full align-middle"
            title={`Overridden (Square: ${squareValue || 'N/A'})`}
          />
        )}
      </p>
      <div className="flex items-center gap-1">
        <p className="text-xs font-medium text-gray-900">
          {effectiveValue || 'N/A'}
        </p>
        <button
          onClick={startEdit}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-800 p-0.5"
          title="Edit"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default EditableField;
